import { env } from '@fim/config';
import { AppError } from '@fim/utils';

import {
  AiProviderError,
  postJson,
  readJson,
  resolveFetch,
  resolveTimeout,
} from '../internal/http.js';
import { parseSseStream } from '../internal/sse.js';
import type { AiProvider, ProviderOptions } from '../provider.js';
import type {
  AiFinishReason,
  AiModel,
  ChatChunk,
  ChatRequest,
  ChatResult,
  EmbedRequest,
  EmbedResponse,
} from '../types.js';

const ANTHROPIC_VERSION = '2023-06-01';
const DEFAULT_BASE_URL = 'https://api.anthropic.com';
export const ANTHROPIC_DEFAULT_MODEL = 'claude-opus-4-8';
const DEFAULT_MAX_TOKENS = 4096;

const ANTHROPIC_MODELS: readonly AiModel[] = [
  {
    id: 'claude-opus-4-8',
    provider: 'anthropic',
    contextWindow: 1_000_000,
    maxOutputTokens: 128_000,
    supportsStreaming: true,
    supportsEmbeddings: false,
  },
  {
    id: 'claude-sonnet-4-6',
    provider: 'anthropic',
    contextWindow: 1_000_000,
    maxOutputTokens: 64_000,
    supportsStreaming: true,
    supportsEmbeddings: false,
  },
  {
    id: 'claude-haiku-4-5',
    provider: 'anthropic',
    contextWindow: 200_000,
    maxOutputTokens: 64_000,
    supportsStreaming: true,
    supportsEmbeddings: false,
  },
];

/* -------------------------------------------------------------------------- */
/* Wire shapes                                                                */
/* -------------------------------------------------------------------------- */

interface AnthropicMessage {
  readonly role: 'user' | 'assistant';
  readonly content: string;
}

interface AnthropicRequestBody {
  model: string;
  max_tokens: number;
  messages: AnthropicMessage[];
  system?: string;
  temperature?: number;
  stream?: boolean;
  metadata?: { user_id?: string } & Record<string, string>;
}

interface AnthropicContentBlock {
  readonly type: string;
  readonly text?: string;
}

interface AnthropicUsage {
  readonly input_tokens: number;
  readonly output_tokens: number;
}

interface AnthropicMessageResponse {
  readonly model: string;
  readonly stop_reason: string | null;
  readonly content: readonly AnthropicContentBlock[];
  readonly usage?: AnthropicUsage;
}

/** SSE event payloads emitted by the Anthropic Messages streaming API. */
type AnthropicStreamEvent =
  | { type: 'message_start'; message: { model: string; usage?: AnthropicUsage } }
  | { type: 'content_block_start'; index: number; content_block: { type: string } }
  | {
      type: 'content_block_delta';
      index: number;
      delta: { type: string; text?: string };
    }
  | { type: 'content_block_stop'; index: number }
  | {
      type: 'message_delta';
      delta: { stop_reason?: string | null };
      usage?: AnthropicUsage;
    }
  | { type: 'message_stop' }
  | { type: 'ping' }
  | { type: 'error'; error: { type: string; message: string } };

/**
 * Anthropic Messages API provider. Implements {@link AiProvider} via raw
 * `fetch` against `/v1/messages`, with real SSE parsing for {@link stream}.
 */
class AnthropicProvider implements AiProvider {
  readonly name = 'anthropic' as const;
  readonly defaultModel: string;
  readonly models = ANTHROPIC_MODELS;

  private readonly apiKey: string;
  private readonly baseUrl: string;
  private readonly timeoutMs: number;
  private readonly fetchImpl: typeof fetch;
  private readonly extraHeaders: Record<string, string>;

  constructor(options: ProviderOptions = {}) {
    const apiKey = options.apiKey ?? env.ANTHROPIC_API_KEY;
    if (!apiKey) {
      throw new AppError('ANTHROPIC_API_KEY is not configured', {
        code: 'AI_MISSING_API_KEY',
        statusCode: 500,
      });
    }
    this.apiKey = apiKey;
    this.baseUrl = stripTrailingSlash(options.baseUrl ?? DEFAULT_BASE_URL);
    this.defaultModel =
      options.defaultModel ?? env.ANTHROPIC_MODEL ?? ANTHROPIC_DEFAULT_MODEL;
    this.timeoutMs = resolveTimeout(options);
    this.fetchImpl = resolveFetch(options);
    this.extraHeaders = { ...options.headers };
  }

  async chat(request: ChatRequest): Promise<string> {
    return (await this.chatVerbose(request)).content;
  }

  async chatVerbose(request: ChatRequest): Promise<ChatResult> {
    const response = await postJson({
      provider: this.name,
      url: `${this.baseUrl}/v1/messages`,
      headers: this.headers(),
      body: this.buildBody(request, false),
      timeoutMs: this.timeoutMs,
      fetchImpl: this.fetchImpl,
      signal: request.signal,
    });

    const data = await readJson<AnthropicMessageResponse>(response);
    const content = data.content
      .filter((block) => block.type === 'text' && typeof block.text === 'string')
      .map((block) => block.text ?? '')
      .join('');

    return {
      content,
      model: data.model,
      finishReason: mapStopReason(data.stop_reason),
      usage: data.usage
        ? {
            inputTokens: data.usage.input_tokens,
            outputTokens: data.usage.output_tokens,
            totalTokens: data.usage.input_tokens + data.usage.output_tokens,
          }
        : undefined,
    };
  }

  async *stream(request: ChatRequest): AsyncIterable<ChatChunk> {
    const response = await postJson({
      provider: this.name,
      url: `${this.baseUrl}/v1/messages`,
      headers: this.headers(),
      body: this.buildBody(request, true),
      timeoutMs: this.timeoutMs,
      fetchImpl: this.fetchImpl,
      signal: request.signal,
    });

    if (!response.body) {
      throw new AiProviderError({
        provider: this.name,
        status: response.status,
        message: 'Anthropic stream response had no body',
      });
    }

    let inputTokens = 0;
    let outputTokens = 0;
    let finishReason: AiFinishReason = 'unknown';

    for await (const sse of parseSseStream(response.body)) {
      if (!sse.data || sse.data === '[DONE]') continue;

      const event = safeParse<AnthropicStreamEvent>(sse.data);
      if (!event) continue;

      switch (event.type) {
        case 'message_start': {
          if (event.message.usage) inputTokens = event.message.usage.input_tokens;
          break;
        }
        case 'content_block_delta': {
          if (event.delta.type === 'text_delta' && event.delta.text) {
            yield { delta: event.delta.text, done: false };
          }
          break;
        }
        case 'message_delta': {
          if (event.delta.stop_reason) {
            finishReason = mapStopReason(event.delta.stop_reason);
          }
          if (event.usage) outputTokens = event.usage.output_tokens;
          break;
        }
        case 'error': {
          throw new AiProviderError({
            provider: this.name,
            status: 502,
            message: `Anthropic stream error: ${event.error.message}`,
            providerCode: event.error.type,
          });
        }
        case 'message_stop': {
          yield this.terminalChunk(finishReason, inputTokens, outputTokens);
          return;
        }
        default:
          break;
      }
    }

    // Stream ended without an explicit `message_stop` — still emit a terminal.
    yield this.terminalChunk(finishReason, inputTokens, outputTokens);
  }

  embed(_request: EmbedRequest): Promise<EmbedResponse> {
    throw new AppError(
      'Anthropic does not expose a native embeddings endpoint; configure AI_PROVIDER=openai for embeddings',
      { code: 'AI_EMBEDDINGS_UNSUPPORTED', statusCode: 400 },
    );
  }

  private terminalChunk(
    finishReason: AiFinishReason,
    inputTokens: number,
    outputTokens: number,
  ): ChatChunk {
    return {
      delta: '',
      done: true,
      finishReason,
      usage: {
        inputTokens,
        outputTokens,
        totalTokens: inputTokens + outputTokens,
      },
    };
  }

  private headers(): Record<string, string> {
    return {
      'x-api-key': this.apiKey,
      'anthropic-version': ANTHROPIC_VERSION,
      ...this.extraHeaders,
    };
  }

  private buildBody(request: ChatRequest, stream: boolean): AnthropicRequestBody {
    const { system, messages } = splitSystem(request);

    const body: AnthropicRequestBody = {
      model: request.model ?? this.defaultModel,
      max_tokens: request.maxTokens ?? DEFAULT_MAX_TOKENS,
      messages,
      stream,
    };

    if (system) body.system = system;
    if (typeof request.temperature === 'number') {
      body.temperature = request.temperature;
    }
    if (request.metadata && Object.keys(request.metadata).length > 0) {
      body.metadata = { ...request.metadata };
    }

    return body;
  }
}

/**
 * Factory for the Anthropic provider. Reads `ANTHROPIC_API_KEY` from
 * `@fim/config` unless overridden via {@link ProviderOptions}.
 */
export const anthropicProvider = (options?: ProviderOptions): AiProvider =>
  new AnthropicProvider(options);

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Anthropic carries the system prompt as a top-level field rather than a
 * `system`-role message. Hoist any system content and coalesce consecutive
 * same-role turns (the API combines them, but normalising keeps things clean).
 */
function splitSystem(request: ChatRequest): {
  system: string | undefined;
  messages: AnthropicMessage[];
} {
  const systemParts: string[] = [];
  if (request.system) systemParts.push(request.system);

  const messages: AnthropicMessage[] = [];
  for (const message of request.messages) {
    if (message.role === 'system') {
      systemParts.push(message.content);
      continue;
    }
    // Tool messages flow only to tool-capable providers (DeepSeek); skip to stay well-typed.
    if (message.role === 'tool') continue;
    messages.push({ role: message.role, content: message.content });
  }

  return {
    system: systemParts.length > 0 ? systemParts.join('\n\n') : undefined,
    messages: ensureLeadingUser(messages),
  };
}

/** Anthropic requires the first message to be a `user` turn. */
function ensureLeadingUser(messages: AnthropicMessage[]): AnthropicMessage[] {
  if (messages.length === 0) {
    return [{ role: 'user', content: '' }];
  }
  if (messages[0]!.role !== 'user') {
    return [{ role: 'user', content: '' }, ...messages];
  }
  return messages;
}

function mapStopReason(reason: string | null): AiFinishReason {
  switch (reason) {
    case 'end_turn':
    case 'stop_sequence':
      return 'stop';
    case 'max_tokens':
      return 'length';
    case 'tool_use':
      return 'tool_use';
    case 'refusal':
      return 'content_filter';
    case null:
    case undefined:
      return 'unknown';
    default:
      return 'unknown';
  }
}

function safeParse<T>(raw: string): T | null {
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function stripTrailingSlash(url: string): string {
  return url.endsWith('/') ? url.slice(0, -1) : url;
}
